import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

const BIO = [
  `Perry Frazier, professionally known as P. Frazy, is an independent songwriter and music producer whose music blends contemporary R&B, soul, inspirational, and gospel influences to tell authentic stories of love, faith, relationships, and personal transformation. His songs are rooted in the belief that the greatest love stories are those shaped by God's Word and lived out through compassion, forgiveness, grace, and unwavering commitment.`,
  `Drawing from life's victories, disappointments, and spiritual journey, P. Frazy writes music that speaks to the heart. His lyrics explore the beauty and challenges of relationships while encouraging listeners to pursue love that is built on character, purpose, and God's design rather than the changing values of today's culture.`,
  `Beyond music, Perry is an entrepreneur, visionary, and founder of Heart N Soul Records, an independent label dedicated to creating music that inspires hope, strengthens relationships, and glorifies God through meaningful storytelling. His mission is to produce songs that not only entertain but also encourage, heal, and remind people that love is ultimately a reflection of God's heart.`,
  `Influenced by classic R&B, soul, contemporary gospel, and timeless songwriting, P. Frazy creates music that connects across generations. Whether singing about romance, redemption, or the lifelong journey of becoming who God created us to be, every song is written with the desire to leave listeners inspired, uplifted, and closer to the One who is the Author of love itself.`,
];

const CREDITS = ["Songwriter.", "Producer.", "Storyteller."];

export default function About() {
  const navigate = useNavigate();
  return (
    <main className="pt-[74px]" data-testid="about-page">
      <section className="section-pad">
        <div className="mx-auto px-5 sm:px-8" style={{ maxWidth: "72ch" }}>
          <div className="frame p-6 sm:p-10">
            <Reveal>
              <p className="font-caps text-gold" style={{ fontSize: ".72rem" }}>About the Label</p>
              <h1 className="font-display foil-text mt-3" style={{ fontSize: "var(--t-h2)", lineHeight: 1.05 }}>Love. Faith. Purpose.</h1>
              <p className="mt-5" style={{ color: "var(--parchment)", fontSize: "1.2rem", lineHeight: 1.7 }}>
                Heart N Soul Records is an independent label built on the belief that the greatest love stories are the ones shaped by God&rsquo;s Word.
              </p>
              <div className="flex items-center gap-3 my-8"><hr className="foil-rule flex-1" /><Heart size={13} fill="var(--rose)" color="var(--rose)" /><hr className="foil-rule flex-1" /></div>
            </Reveal>

            {/* Founder block */}
            <Reveal>
              <p className="font-caps text-gold" style={{ fontSize: ".62rem" }}>Founder</p>
              <h2 className="font-display mt-1 whitespace-nowrap" style={{ fontSize: "clamp(1.2rem, 5.6vw, 1.9rem)", color: "var(--gold-lift)", lineHeight: 1.15 }} data-testid="founder-name">
                Perry Frazier <span style={{ color: "var(--gold)" }}>- aka P. Frazy</span>
              </h2>
              <p className="font-caps text-ash mt-1" style={{ fontSize: ".6rem" }}>Songwriter - Producer - Founder of Heart N Soul Records</p>

              <div className="sm:float-left sm:mr-6 my-5 sm:my-2" style={{ width: "min(320px, 60%)" }}>
                <div className="frame p-2" style={{ boxShadow: "var(--shadow-warm)" }}>
                  <img src="/assets/img/headshot-1280.webp" onError={(e) => { e.currentTarget.src = "/assets/img/headshot-1280.jpg"; }} alt="Perry Frazier — P. Frazy" className="w-full h-auto" style={{ borderRadius: "var(--radius)" }} />
                </div>
                <p style={{ color: "var(--ash)", fontSize: "var(--t-cap)" }} className="mt-2">Perry Frazier — P. Frazy. Founder, Heart N Soul Records.</p>
              </div>

              <div style={{ color: "var(--parchment)", lineHeight: 1.7, textWrap: "pretty" }}>
                <p style={{ marginBottom: "var(--s3)" }} className="dropcap-p">{BIO[0]}</p>
                {BIO.slice(1).map((p, i) => <p key={i} style={{ marginBottom: "var(--s3)" }}>{p}</p>)}
              </div>
              <div style={{ clear: "both" }} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Pull-quote band — restrained, decorative image at 0.14 opacity, no animation */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "var(--ink)", paddingBlock: "var(--s6)" }} data-testid="pullquote-band">
        <div aria-hidden="true" className="absolute inset-0" style={{ pointerEvents: "none" }}>
          <img src="/assets/img/single-love-1280.webp" alt="" aria-hidden="true"
               style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "left top", opacity: 0.14 }} />
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 45%, rgba(11,9,8,0.35) 0%, rgba(11,9,8,0.9) 70%, #0B0908 100%)" }} />
        </div>
        <div className="relative mx-auto px-6 text-center" style={{ maxWidth: "60ch" }}>
          <Heart size={20} fill="var(--rose)" color="var(--rose)" className="mx-auto mb-6" />
          <p className="font-script text-gold" style={{ fontSize: "clamp(1.4rem, 8.5vw, 3.25rem)", lineHeight: 1.25 }} data-testid="about-creed">
            <span className="block whitespace-nowrap">Every song has a story.</span>
            <span className="block whitespace-nowrap">Every story has a purpose.</span>
            <span className="block whitespace-nowrap">Every purpose points back to God.</span>
          </p>
          <hr className="foil-rule mt-8 mx-auto" style={{ maxWidth: 220 }} />
        </div>
      </section>

      {/* Label block */}
      <section className="section-pad">
        <div className="mx-auto px-5 sm:px-8 text-center" style={{ maxWidth: "68ch" }}>
          <Reveal>
            <img src="/assets/img/logo.png" alt="Heart N Soul Records" style={{ width: 220 }} className="h-auto mx-auto" />
            <p className="font-script foil-text mt-6" style={{ fontSize: "2rem", lineHeight: 1.05 }}>Music with Heart, Stories with Soul.</p>
            <p className="mt-8" style={{ color: "var(--parchment)", fontSize: "1.15rem", lineHeight: 1.7 }}>
              Heart N Soul Records exists to create music that inspires hope, strengthens relationships, and glorifies God through meaningful storytelling. Songs that don&rsquo;t only entertain — songs that encourage, heal, and remind people that love is a reflection of God&rsquo;s heart.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-8 font-caps" style={{ fontSize: ".72rem", color: "var(--parchment)" }}>
              {CREDITS.map((c, i) => (
                <span key={c} className="inline-flex items-center gap-3">{c}{i < CREDITS.length - 1 && <Heart size={12} fill="var(--rose)" color="var(--rose)" />}</span>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <button className="btn-gold" onClick={() => navigate("/", { state: { scrollTo: "music" } })} data-testid="about-hear-music">Hear the music</button>
              <Link className="btn-ghost" to="/merch">Shop the merch</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
